import {
  DateTileData,
  DateStatus,
  Todo,
  Note,
  Reminder,
  Habit,
  Tracker,
  TrackerEntry,
  JournalEntry,
} from "../types";
import { RootState } from "../store";
import { createSelector } from "@reduxjs/toolkit";
import { getDayOfWeek, isHabitActiveForDate } from "./dateUtils";

/**
 * Redux Selectors for data access
 */
export const selectTodos = (state: RootState) => state.todos.items;
export const selectHabits = (state: RootState) => state.habits.items;
export const selectHabitCompletions = (state: RootState) =>
  state.habits.completions;
export const selectReminders = (state: RootState) => state.reminders.items;
export const selectNotes = (state: RootState) => state.notes.items;
export const selectJournal = (state: RootState) => state.journal.items;
export const selectTrackers = (state: RootState) => state.trackers.items;
export const selectTrackerEntries = (state: RootState) =>
  state.trackers.entries;
export const selectUser = (state: RootState) => state.user;

/**
 * Shared selector for calendar and day view data - only selects the data needed for date calculations
 */
export const selectCalendarDayViewData = createSelector(
  (state: any) => state.todos,
  (state: any) => state.notes,
  (state: any) => state.reminders,
  (state: any) => state.habits,
  (state: any) => state.trackers,
  (state: any) => state.journal,
  (todos, notes, reminders, habits, trackers, journal) => ({
    todos: {
      ...todos,
      items: todos.items || [],
    },
    notes: {
      ...notes,
      items: notes.items || [],
    },
    reminders: {
      ...reminders,
      items: reminders.items || [],
    },
    habits: {
      ...habits,
      items: habits.items || [], // Temporarily remove isActive filter to debug
      completions: habits.completions || [],
    },
    trackers: {
      ...trackers,
      items: trackers.items
        ? trackers.items.filter((tracker: any) => {
            if (!tracker.isActive) return false;
            // Only include trackers that were created within the date range we're viewing
            // For calendar view, we show trackers that could potentially have entries in the current month
            // This is a broader filter than day view since we don't know the specific date yet
            return true; // For now, keep all active trackers in calendar view
          })
        : [],
      entries: trackers.entries || [],
    },
    journal: {
      ...journal,
      items: journal.items || [],
    },
  }),
);

/**
 * Get date data from Redux state
 */
export const getDateDataFromState = (
  state: Omit<RootState, "user">,
  dateString: string,
) => {
  return {
    todos: (state.todos.items || []).filter(
      (todo: Todo) => todo.date === dateString,
    ),
    notes: (state.notes.items || []).filter(
      (note: Note) => note.date === dateString,
    ),
    journal: (state.journal.items || []).filter(
      (entry: JournalEntry) => entry.date === dateString,
    ),
    reminders: (state.reminders.items || []).filter(
      (reminder: Reminder) => reminder.date === dateString,
    ),
    habitCompletions: (state.habits.completions || []).filter((completion) => {
      // Handle different date formats - completion.date might be in different format
      if (!completion.date) return false;
      const completionDate = completion.date.split("T")[0]; // Remove time part if present
      return completionDate === dateString;
    }),
    habits: state.habits.items
      ? state.habits.items.filter((habit: Habit) => {
          if (!habit.isActive) return false;
          // Only show habits that were created on or before the selected date
          const habitCreatedDate = new Date(habit.createdAt);
          habitCreatedDate.setHours(0, 0, 0, 0);
          const targetDate = new Date(dateString);
          targetDate.setHours(0, 0, 0, 0);
          if (habitCreatedDate > targetDate) return false;

          // Check if habit is active for this day of the week
          return isHabitActiveForDate(habit.activeForDays, targetDate);
        })
      : [],
    trackers: (state.trackers.items || []).filter((tracker: Tracker) => {
      if (!tracker.isActive) return false;
      // Only show trackers that were created on or before the selected date
      const trackerCreatedDate = new Date(tracker.createdAt);
      trackerCreatedDate.setHours(0, 0, 0, 0);
      const targetDate = new Date(dateString);
      targetDate.setHours(0, 0, 0, 0);
      if (trackerCreatedDate > targetDate) return false;

      // Check tracking days: if specified, only show on those days
      if (tracker.trackingDays && tracker.trackingDays.length > 0) {
        const dayOfWeek = getDayOfWeek(targetDate);
        return tracker.trackingDays.includes(dayOfWeek);
      }

      return true;
    }),
    trackerEntries: (state.trackers.entries || []).filter(
      (entry: TrackerEntry) => {
        if (entry.date !== dateString) return false;

        // Find the tracker for this entry
        const tracker = (state.trackers.items || []).find(
          (t: Tracker) => t.id === entry.trackerId,
        );
        if (!tracker || !tracker.isActive) return false;

        // Only include entries for dates on or after the tracker was created
        const trackerCreatedDate = new Date(tracker.createdAt);
        trackerCreatedDate.setHours(0, 0, 0, 0);
        const entryDate = new Date(dateString);
        entryDate.setHours(0, 0, 0, 0);

        return trackerCreatedDate <= entryDate;
      },
    ),
  };
};

/**
 * Calculate date tile data based on Redux state
 */
export const calculateDateTileDataFromState = (
  state: Omit<RootState, "user">,
  dateString: string,
  isCurrentMonth: boolean,
  isToday: boolean,
): DateTileData => {
  const statuses: DateStatus[] = [];

  // Get data for this specific date from Redux state
  const dateTodos = (state.todos.items || []).filter(
    (todo: Todo) => todo.date === dateString,
  );
  const dateNotes = (state.notes.items || []).filter(
    (note: Note) => note.date === dateString,
  );
  const dateReminders = (state.reminders.items || []).filter(
    (reminder: Reminder) => reminder.date === dateString,
  );
  const dateHabitCompletions = (state.habits.completions || []).filter(
    (completion: any) => {
      // Handle different date formats - completion.date might be in different format
      if (!completion.date) return false;
      const completionDate = completion.date.split("T")[0]; // Remove time part if present
      return completionDate === dateString;
    },
  );
  // Filter tracker entries to only include entries for trackers that exist, were created on or before the entry date, and should be tracked on this day
  const dateTrackerEntries = (state.trackers.entries || []).filter(
    (entry: TrackerEntry) => {
      if (entry.date !== dateString) return false;

      // Find the tracker for this entry
      const tracker = (state.trackers.items || []).find(
        (t: Tracker) => t.id === entry.trackerId,
      );
      if (!tracker || !tracker.isActive) return false;

      // Only include entries for dates on or after the tracker was created
      const trackerCreatedDate = new Date(tracker.createdAt);
      trackerCreatedDate.setHours(0, 0, 0, 0);
      const entryDate = new Date(dateString);
      entryDate.setHours(0, 0, 0, 0);
      if (trackerCreatedDate > entryDate) return false;

      // Check tracking days: if specified, only include entries for days the tracker should be tracked
      if (tracker.trackingDays && tracker.trackingDays.length > 0) {
        const dayOfWeek = getDayOfWeek(entryDate);
        return tracker.trackingDays.includes(dayOfWeek);
      }

      return true;
    },
  );

  // Calculate counts
  const todosCount = dateTodos.filter((todo: Todo) => !todo.completed).length;
  const completedTodosCount = dateTodos.filter(
    (todo: Todo) => todo.completed,
  ).length;
  const totalTodosCount = dateTodos.length;

  const notesCount = dateNotes.length;
  const remindersCount = dateReminders.length;
  const trackersCount = dateTrackerEntries.length;

  // Count habits that should be visible for this date (active habits created on or before this date and active on this day)
  const habitsCount = state.habits.items
    ? state.habits.items.filter((habit: Habit) => {
        if (!habit.isActive) return false;
        if (habit.isPaused) return false;

        // Show habits for dates on or after creation date
        const habitCreatedDate = new Date(habit.createdAt);
        habitCreatedDate.setHours(0, 0, 0, 0);
        const targetDate = new Date(dateString);
        targetDate.setHours(0, 0, 0, 0);
        if (habitCreatedDate > targetDate) return false;

        // Check if habit is active for this day of the week
        return isHabitActiveForDate(habit.activeForDays, targetDate);
      }).length
    : 0;
  const completedHabitsCount = dateHabitCompletions.filter(
    (completion: any) => completion.completed,
  ).length;

  // Only calculate statuses for current month dates
  if (isCurrentMonth) {
    // Check for bingo (all habits and todos completed)
    const allHabitsCompleted =
      completedHabitsCount === habitsCount && habitsCount > 0;
    const allTodosCompleted =
      totalTodosCount > 0 ? completedTodosCount === totalTodosCount : true;
    const isBingo =
      allHabitsCompleted &&
      allTodosCompleted &&
      (habitsCount > 0 || totalTodosCount > 0);

    if (isBingo) {
      statuses.push("bingo");
    } else {
      // Check for all todos completed
      if (totalTodosCount > 0 && completedTodosCount === totalTodosCount) {
        statuses.push("todosCompleted");
      }

      // Check for streak (all habits completed but maybe some todos remaining)
      if (allHabitsCompleted && habitsCount > 0) {
        statuses.push("streak");
      }

      // Check for missed habits - only for past dates and today, and only for dates after habit creation
      const targetDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

      // Only show missed status for past dates and today, not future dates
      if (
        habitsCount > 0 &&
        completedHabitsCount < habitsCount &&
        targetDate <= today
      ) {
        // Check if any active habits were created on or before this date
        const activeHabits = state.habits.items
          ? state.habits.items.filter((habit: any) => habit.isActive)
          : [];
        const hasRelevantHabits = activeHabits.some((habit: any) => {
          const habitCreatedDate = new Date(habit.createdAt);
          habitCreatedDate.setHours(0, 0, 0, 0);
          return targetDate >= habitCreatedDate;
        });

        if (hasRelevantHabits) {
          statuses.push("missed");
        }
      }

      // Check for incomplete todos
      if (todosCount > 0) {
        statuses.push("hasTodos");
      }

      // Check for partial completion
      if (
        (completedHabitsCount > 0 && completedHabitsCount < habitsCount) ||
        (completedTodosCount > 0 && todosCount > 0)
      ) {
        statuses.push("partial");
      }
    }

    // Add notes indicator
    if (notesCount > 0) {
      statuses.push("hasNotes");
    }

    // Add reminders indicator
    if (remindersCount > 0) {
      statuses.push("hasReminders");
    }

    // Add tracker entries indicator
    if (trackersCount > 0) {
      statuses.push("hasTrackers");
    }
  }

  return {
    date: dateString,
    statuses,
    todosCount,
    completedTodosCount,
    habitsCount,
    completedHabitsCount,
    notesCount,
    remindersCount,
    trackersCount,
    isBingo: statuses.includes("bingo"),
    isToday,
    isCurrentMonth,
  };
};
